import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { Component, ViewEncapsulation, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import {
    OfflineStatusService,
    LoggedInUserService,
    ConfigService,
    select,
    WebSocketService,
    BroadcastService,
    PageVisibilityService,
    ClientConstants
} from '@formbird/services';
import { OfflineStatus } from '@formbird/types';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'ft-offline-status',
  standalone: true,
  templateUrl: 'offline-status.component.html',
  styleUrls: ['./offline-status.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDividerModule,
    MatBadgeModule,
    MatCardModule,
    MatProgressBarModule
  ]
})
export class OfflineStatusComponent implements OnInit, OnDestroy {
    private offlineStatusService = inject(OfflineStatusService);
    private loggedInUserService = inject(LoggedInUserService);
    private configService = inject(ConfigService);
    private cd = inject(ChangeDetectorRef);
    private webSocketService = inject(WebSocketService);
    private broadcastService = inject(BroadcastService);
    private pageVisibilityService = inject(PageVisibilityService);

    @select(['offlineReducer', 'offlineStatus']) offlineStatus$: Observable<OfflineStatus>;

    isMenuOpened = false;
    isOffline = false;
    cloudIcon = 'cloud_queue';
    offlineStatus: OfflineStatus;
    operateOnline: boolean;
    cacheEnabled: boolean;
    isCachingNotAllowed: boolean;

    // Cache progress status values
    appValue: number = 0;
    dataValue: number = 0;
    mapValue: number = 0;
    fileValue: number = 0;

    private subs: Subscription = new Subscription();
    private visibilityListener = async (isHidden) => {
        if (!isHidden) {
            // load offline status from key value storage
            const offlineStatus = await this.offlineStatusService.loadOfflineStatusFromKeyValueStorage();
            if (offlineStatus) {
                this.updateStatus(offlineStatus);
            }

            const shouldOperateOnline = this.offlineStatusService.shouldOperateOnline();
            if (this.operateOnline !== shouldOperateOnline) {
                this.toggleOffline();
            }
        }
    };

    constructor() {
        this.cacheEnabled = this.offlineStatusService.isCachingEnabled();
        const clientConfig = this.configService.clientConfig();
        this.isCachingNotAllowed = clientConfig?.cachingNotAllowed;
        this.operateOnline = this.offlineStatusService.shouldOperateOnline();
        this.isOffline = !this.operateOnline; // Sync local state
        this.offlineStatusService.postMessageToServiceWorker(this.operateOnline);


        // #30685 - get counts from IndexedDB, caches, and update offlineStatus
        this.offlineStatusService.initializeOfflineCounts();
    }

    ngOnInit() {
        const subOfflineStatus = this.offlineStatusService.offlineStatus$.subscribe((offlineStatusData) => {
            this.updateStatus(offlineStatusData);
        });

        const subOfflineRedux = this.offlineStatus$.subscribe((offlineStatusData) => {
            this.updateStatus(offlineStatusData);
        });

        this.loggedInUserService.addUserConfigItemListener('cachingEnabled', newValue => {
            this.cacheEnabled = newValue;
            if (this.offlineStatus) {
                this.updateStatus(this.offlineStatus);
            }
        });

        this.subs.add(subOfflineStatus);
        this.subs.add(subOfflineRedux);

        this.pageVisibilityService.registerVisibilityListener(this.visibilityListener);
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
        this.pageVisibilityService.removeVisibilityListener(this.visibilityListener);
    }

    updateStatus(offlineStatusData: OfflineStatus) {
        // Add max value aliases if not present
        const status = { ...offlineStatusData } as any; // Clone to allow mutation

        // Preserve the highest observed dataMaxCount so it never decreases
        const previousDataMaxCount = (this.offlineStatus as any)?.dataMaxCount ?? 0;
        const incomingDataMaxCount = (offlineStatusData as any).dataMaxCount ?? (offlineStatusData as any).maxCacheCount ?? 0;
        const stableDataMaxCount = Math.max(previousDataMaxCount, incomingDataMaxCount);

        status.appMaxCount = status.appMaxCount ?? status.totalStaticResourceCount ?? 0;
        status.dataMaxCount = stableDataMaxCount;
        status.mapMaxCount = status.mapMaxCount ?? status.tileServerMaxCacheCount ?? 0;
        status.fileMaxCount = status.fileMaxCount ?? status.maxCacheAttachedFileCount ?? 0;

        this.offlineStatus = status;

        // Store current counts
        this.appValue = status.currentStaticResourceCount || 0;
        this.dataValue = status.currentCacheCount || 0;
        this.mapValue = status.tileServerCacheCount || 0;
        this.fileValue = status.currentCacheAttachedFileCount || 0;

        if (!status.connected) {
            this.cloudIcon = 'cloud_off';
        } else if (!this.offlineStatusService.shouldOperateOnline()) {
            this.cloudIcon = 'cloud_operate_offline';
        } else if (status.pendingDocumentCount > 0 || (typeof status.cacheLoading === 'number' && status.cacheLoading > 0) || status.pendingUploadCount > 0) {
            this.cloudIcon = 'cloud_sync';
        } else {
            this.cloudIcon = 'cloud_done';
        }
        this.cd.detectChanges();
    }

    getDisplayCount(current: number, max: number): string {
        if (!max || max <= 0) return '0 / 0';
        const displayCurrent = current === 0 ? max : current;
        return `${displayCurrent} / ${max}`;
    }

    getStatusClass(current: number, max: number): string {
        if (!max || max <= 0) return 'high';

        // current === 0 means all loaded as per getDisplayCount
        if (current === 0 || current >= max) {
            return 'high';
        }

        const progress = (current / max) * 100;
        if (progress < 30) {
            return 'low';
        } else if (progress < 95) {
            return 'medium';
        } else {
            return 'high';
        }
    }

    menuOpened() {
        this.isMenuOpened = true;
    }

    menuClosed() {
        this.isMenuOpened = false;
    }

    toggleOffline(event?: Event) {
        event?.stopPropagation();
        this.operateOnline = !this.operateOnline;
        this.isOffline = !this.operateOnline;

        this.offlineStatusService.setOperateOnline(this.operateOnline);
        this.offlineStatusService.postMessageToServiceWorker(this.operateOnline);

        if (this.operateOnline) {
            this.webSocketService.start();
            this.broadcastService.broadcast(ClientConstants.CHECK_UNSYNCED_DOCUMENTS, {operateOnline: true});
        }
    }

    clearErrorCachingAttachedFiles() {
        this.offlineStatusService.offlineStatus.errorCacheAttachedFile = [];
        this.loggedInUserService.setUserConfigItem('ErrorCachingAttachedFiles', this.offlineStatusService.offlineStatus.errorCacheAttachedFile);
    }

    clearErrorOfflineChangesSyncDocs() {
        this.offlineStatusService.offlineStatus.errorOfflineChangesSyncDocs = [];
        this.loggedInUserService.setUserConfigItem('ErrorOfflineChangesSyncDocs', this.offlineStatusService.offlineStatus.errorOfflineChangesSyncDocs);
    }

    closeProgress() {
      const divSWUpdateIndicator = document.getElementById('swUpdateProgressIndicator');
      if (divSWUpdateIndicator) {
        divSWUpdateIndicator.classList.remove('show');
      }
    }

    //disable if the menu has no content to prevent dropdown 
    isMenuEnabled(): boolean {
      return (this.offlineStatus?.cachingApp && this.offlineStatus?.currentStaticResourceCount > 0) ||
             (typeof this.offlineStatus?.cacheLoading === 'number' && this.offlineStatus.cacheLoading > 0) ||
             (this.offlineStatus?.maxCacheCount > 0) ||
             (this.offlineStatus?.pendingDocumentCount > 0) ||
             (this.offlineStatus?.tileServerCacheCount > 0) ||
             (this.offlineStatus?.errorCacheAttachedFile.length > 0);
  }
}
